/**
 * Load the dictionaries this render needs before the app paints.
 *
 * Dictionaries:
 * - storefront → slim shopper set in the store language (Arabic is bundled)
 *   plus the slim set in the app language so owner chrome (`tEditor`) matches
 * - job platform → the full dictionary for the active language
 *
 * Never parse a full locale on a storefront request — that was a 200 KiB+ hit
 * on every one of them. Owners pull the full set after hydration.
 */
import {
  ensureFullKawadernaMessages,
  ensureKawadernaMessages,
  isKawadernaLang,
  primeKawadernaLangState,
  primeKawadernaMessageState,
} from '~/composables/useKawadernaI18n'
import { isStorefrontRequest } from '~/composables/useStorefrontHost'
import { useStorefrontLangOverride } from '~/composables/storefront/langState'
import { syncPublicLegalLangLock } from '~/composables/usePublicLegalLocale'

export default defineNuxtPlugin({
  name: 'i18n-messages',
  enforce: 'pre',
  parallel: false,
  async setup() {
    // Register payload slots before any await. `useState` after a locale import
    // resolves is a different object than the one `t()` / `tEditor` read on SSR.
    const flags = primeKawadernaMessageState()
    const lang = primeKawadernaLangState()
    const forcedLegal = syncPublicLegalLangLock()
    if (forcedLegal) lang.value = forcedLegal

    const path = import.meta.server
      ? useRequestURL().pathname
      : window.location.pathname

    const platform = lang.value

    if (isStorefrontRequest(path)) {
      const override = useStorefrontLangOverride().value
      const shopper = isKawadernaLang(override) ? override : null
      // Owner chrome follows the app language. Load it here so SSR HTML is not
      // English fallback while the client hydrates Arabic (or the reverse).
      await ensureKawadernaMessages(platform)
      if (shopper && shopper !== platform) {
        await ensureKawadernaMessages(shopper)
      }
      return
    }

    await ensureFullKawadernaMessages(platform)
    flags.value = { ...flags.value, [platform]: true }

    // The six newer locales are translated in bulk, so English backs any key
    // they are still missing. Arabic and English need no second dictionary.
    if (platform !== 'ar' && platform !== 'en') {
      await ensureFullKawadernaMessages('en')
      flags.value = { ...flags.value, en: true }
    }
  },
})
